import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, XCircle, AlertTriangle, FileText, Shield, Send, Loader2, Award, TrendingUp, Activity, Info, ClipboardCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReviewSubmit = ({ assessment, farmer, previousAssessment, journeyType, journeyConfig, onSubmit, submitting, refreshAssessment, user }) => {
  const navigate = useNavigate();
  const scoreReady = assessment?.score_readiness_status === 'READY';
  const policyReady = assessment?.policy_readiness_status === 'READY';
  const isScored = assessment?.assessment_status === 'SCORED';
  const isSubmitted = assessment?.assessment_status === 'SUBMITTED' || isScored;
  const isAgent = user?.role === 'Field Agent';
  const isOfficerOrAdmin = user?.role === 'Credit Officer' || user?.role === 'System Administrator';
  const [agentSubmitting, setAgentSubmitting] = useState(false);
  const changedCount = (assessment?.changed_fields || []).length;
  const docStatus = assessment?.document_status || {};

  const modelTrace = assessment?.model_trace;
  const policyTrace = assessment?.policy_trace;

  const REVIEW_SECTIONS = [
    {
      title: 'Assessment Details',
      items: [
        { label: 'Journey Type', value: journeyConfig?.label || journeyType },
        { label: 'Cycle', value: `#${assessment?.cycle_number || 'N/A'}` },
        { label: 'Farmer', value: farmer?.farmer_name || 'N/A' },
        { label: 'Previous Cycle', value: assessment?.previous_cycle_reference || 'N/A' },
        { label: 'Reference Date', value: assessment?.reference_date?.slice(0, 10) || 'N/A' },
      ]
    },
    {
      title: 'Version Stamps',
      items: [
        { label: 'Assessment Version', value: assessment?.assessment_version },
        { label: 'Model Version', value: assessment?.model_version },
        { label: 'Policy Version', value: assessment?.policy_version },
        { label: 'Pipeline Version', value: assessment?.pipeline_version },
      ]
    },
    {
      title: 'Limits',
      items: [
        { label: 'Previous Sanctioned', value: assessment?.sanctioned_limit_previous ? `Rs. ${Number(assessment.sanctioned_limit_previous).toLocaleString()}` : 'N/A' },
        { label: 'Requested', value: assessment?.sanctioned_limit_requested ? `Rs. ${Number(assessment.sanctioned_limit_requested).toLocaleString()}` : 'Same as previous' },
      ]
    }
  ];

  if (journeyType === 'ENHANCED_LIMIT') {
    REVIEW_SECTIONS.push({
      title: 'Enhancement',
      items: [
        { label: 'Rationale', value: assessment?.enhancement_rationale || 'Not provided' },
        { label: 'Changed Fields', value: `${changedCount} field(s)` },
      ]
    });
  }

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1" data-testid="review-submit-title">
          Review & Submit
        </h3>
        <p className="text-sm text-gray-500">
          Final review of the assessment before scoring. Verify readiness status below.
        </p>
      </div>

      {/* Readiness Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Score Readiness */}
        <Card className={`border-2 ${scoreReady ? 'border-green-300' : 'border-red-300'}`} data-testid="score-readiness-card">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              {scoreReady ? <CheckCircle2 className="h-5 w-5 text-green-600" /> : <XCircle className="h-5 w-5 text-red-600" />}
              <span className="font-semibold text-sm">Score Readiness</span>
              <Badge className={`text-[10px] ${scoreReady ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {scoreReady ? 'READY' : 'NOT READY'}
              </Badge>
            </div>
            {!scoreReady && (assessment?.score_readiness_reasons || []).length > 0 && (
              <ul className="space-y-1 mt-2">
                {assessment.score_readiness_reasons.map((r, i) => (
                  <li key={i} className="text-xs text-red-700 flex items-start gap-1.5">
                    <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" /> {r}
                  </li>
                ))}
              </ul>
            )}
            {scoreReady && <p className="text-xs text-green-700 mt-1">All minimum data for Yogyank scoring is available.</p>}
          </CardContent>
        </Card>

        {/* Policy Readiness */}
        <Card className={`border-2 ${policyReady ? 'border-green-300' : 'border-amber-300'}`} data-testid="policy-readiness-card">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              {policyReady ? <Shield className="h-5 w-5 text-green-600" /> : <AlertTriangle className="h-5 w-5 text-amber-600" />}
              <span className="font-semibold text-sm">Policy Readiness</span>
              <Badge className={`text-[10px] ${policyReady ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                {policyReady ? 'READY' : 'ADVISORY'}
              </Badge>
            </div>
            {!policyReady && (assessment?.policy_readiness_reasons || []).length > 0 && (
              <ul className="space-y-1 mt-2">
                {assessment.policy_readiness_reasons.map((r, i) => (
                  <li key={i} className="text-xs text-amber-700 flex items-start gap-1.5">
                    <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" /> {r}
                  </li>
                ))}
              </ul>
            )}
            {policyReady && <p className="text-xs text-green-700 mt-1">All lender policy requirements met.</p>}
          </CardContent>
        </Card>
      </div>

      {/* Review Sections */}
      {REVIEW_SECTIONS.map((section, si) => (
        <Card key={si}>
          <CardContent className="pt-4">
            <div className="font-semibold text-xs text-gray-500 uppercase tracking-wide mb-2">{section.title}</div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
              {section.items.map((item, ii) => (
                <div key={ii} className="flex justify-between items-baseline py-1 border-b border-gray-50">
                  <span className="text-xs text-gray-500">{item.label}</span>
                  <span className="text-xs font-medium text-gray-900 font-mono">{item.value || 'N/A'}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Documents Summary */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="h-4 w-4 text-gray-600" />
            <span className="font-semibold text-xs text-gray-500 uppercase tracking-wide">Document Status</span>
          </div>
          <div className="flex gap-4 text-xs">
            <span className="text-green-700">Valid: {docStatus.valid || 0}</span>
            <span className="text-red-700">Expired: {docStatus.expired || 0}</span>
            <span className="text-gray-500">Missing: {docStatus.missing || 0}</span>
          </div>
        </CardContent>
      </Card>

      {/* Changed Fields Summary */}
      {changedCount > 0 && (
        <Card>
          <CardContent className="pt-4">
            <div className="font-semibold text-xs text-gray-500 uppercase tracking-wide mb-2">
              Changed Since Last Cycle ({changedCount})
            </div>
            <div className="space-y-1">
              {(assessment?.changed_fields || []).slice(0, 10).map((cf, i) => (
                <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-gray-50">
                  <span className="text-gray-600">{cf.field}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 line-through">{String(cf.previous_value ?? 'N/A').slice(0, 20)}</span>
                    <span className="text-amber-700 font-medium">{String(cf.current_value ?? 'N/A').slice(0, 20)}</span>
                  </div>
                </div>
              ))}
              {changedCount > 10 && <div className="text-xs text-gray-400 pt-1">+{changedCount - 10} more changes</div>}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Officer Review Placeholder */}
      <Card className="border-dashed border-gray-300">
        <CardContent className="pt-4">
          <div className="flex items-center gap-2 mb-1">
            <Shield className="h-4 w-4 text-gray-400" />
            <span className="font-semibold text-xs text-gray-400 uppercase tracking-wide">Officer Review (Placeholder)</span>
          </div>
          <p className="text-xs text-gray-400">
            Review workflow will be configured per lender policy. Not active in current version.
          </p>
        </CardContent>
      </Card>

      {/* Submit */}
      {!isSubmitted ? (
        isAgent ? (
          <Button
            className="w-full py-6 text-base"
            disabled={!scoreReady || agentSubmitting}
            onClick={async () => {
              if (!assessment?.id) return;
              setAgentSubmitting(true);
              try {
                await axios.post(`${API}/assessments/${assessment.id}/agent-submit`);
                if (refreshAssessment) await refreshAssessment();
                toast.success('Assessment sent to Credit Officer for scoring & review');
              } catch (err) {
                const detail = err.response?.data?.detail;
                if (typeof detail === 'object' && detail.reasons) {
                  toast.error(`Cannot send: ${detail.reasons.slice(0, 3).join(', ')}`);
                } else {
                  toast.error(typeof detail === 'string' ? detail : 'Failed to send for review');
                }
              } finally {
                setAgentSubmitting(false);
              }
            }}
            style={{ background: scoreReady ? '#2563eb' : '#9ca3af' }}
            data-testid="agent-send-for-review-btn"
          >
            {agentSubmitting ? (
              <span className="flex items-center gap-2"><Loader2 className="h-5 w-5 animate-spin" /> Sending...</span>
            ) : (
              <span className="flex items-center gap-2"><ClipboardCheck className="h-5 w-5" /> Send for Review</span>
            )}
          </Button>
        ) : isOfficerOrAdmin ? (
          <Button
            className="w-full py-6 text-base"
            disabled={!scoreReady || submitting}
            onClick={onSubmit}
            style={{ background: scoreReady ? '#2563eb' : '#9ca3af' }}
            data-testid="submit-assessment-btn"
          >
            {submitting ? (
              <span className="flex items-center gap-2"><Loader2 className="h-5 w-5 animate-spin" /> Scoring in progress...</span>
            ) : (
              <span className="flex items-center gap-2"><Send className="h-5 w-5" /> Submit Assessment for Scoring</span>
            )}
          </Button>
        ) : null
      ) : isScored ? (
        <div className="space-y-5" data-testid="scoring-result-section">
          {/* Entitlement Score Card */}
          <Card className="border-2 border-green-400 overflow-hidden">
            <div className="h-1.5 bg-green-500" />
            <CardContent className="pt-5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Entitlement Score</div>
                  <div className="text-5xl font-bold" style={{ color: (assessment?.yogyank_score_base || 0) >= 500 ? '#16a34a' : (assessment?.yogyank_score_base || 0) >= 300 ? '#ca8a04' : '#dc2626' }}>
                    {assessment?.yogyank_score_base ?? 'N/A'}
                  </div>
                  <Badge className="mt-2 text-xs" style={{ background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' }}>
                    {assessment?.yogyank_score_band || 'N/A'}
                  </Badge>
                </div>
                <div className="text-right space-y-1">
                  <div className="text-xs text-gray-500">Yogyank: <span className="font-mono font-semibold">{assessment?.yogyank_score_base ?? 'N/A'}</span></div>
                  {assessment?.yogyank_score_final !== null && assessment?.yogyank_score_final !== undefined && (
                    <div className="text-xs text-gray-500">Post-policy: <span className="font-mono font-semibold">{assessment.yogyank_score_final}</span></div>
                  )}
                  {policyTrace?.policy_adjustment_points !== undefined && (
                    <div className="text-xs text-gray-500">Policy Adj: <span className={`font-mono font-semibold ${policyTrace.policy_adjustment_points >= 0 ? 'text-green-700' : 'text-red-700'}`}>{policyTrace.policy_adjustment_points >= 0 ? '+' : ''}{policyTrace.policy_adjustment_points}</span></div>
                  )}
                  <div className="text-xs text-gray-500">Scored: <span className="font-mono">{assessment?.scoring_date?.slice(0, 10)}</span></div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Model Trace */}
          {modelTrace && (
            <Card data-testid="model-trace-card">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <Activity className="h-4 w-4 text-blue-600" />
                  <span className="font-semibold text-xs text-gray-500 uppercase tracking-wide">Model Trace</span>
                </div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                  {[
                    ['Model Version', modelTrace.model_version],
                    ['Pipeline Version', modelTrace.pipeline_version],
                    ['Completeness', `${modelTrace.completeness_pct}%`],
                    ['Scoring Status', modelTrace.scoring_status],
                  ].map(([label, val]) => (
                    <div key={label} className="flex justify-between items-baseline py-1 border-b border-gray-50">
                      <span className="text-xs text-gray-500">{label}</span>
                      <span className="text-xs font-medium text-gray-900 font-mono">{val || 'N/A'}</span>
                    </div>
                  ))}
                </div>
                {(modelTrace.entitlement_reasons || []).length > 0 && (
                  <div className="mt-3">
                    <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">Top Drivers</div>
                    <div className="space-y-1">
                      {modelTrace.entitlement_reasons.map((r, i) => (
                        <div key={i} className="text-xs text-gray-700 flex items-start gap-1.5">
                          <TrendingUp className="h-3 w-3 mt-0.5 text-blue-500 shrink-0" /> {r}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {(modelTrace.reference_audit?.reference_match_path) && (
                  <div className="mt-2 text-[10px] text-gray-400">
                    Ref match: {modelTrace.reference_audit.reference_match_path} | FY: {modelTrace.reference_audit.actual_reference_fy_used || 'N/A'}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Policy Trace */}
          {policyTrace && (
            <Card data-testid="policy-trace-card">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <Shield className="h-4 w-4 text-purple-600" />
                  <span className="font-semibold text-xs text-gray-500 uppercase tracking-wide">Policy Trace</span>
                </div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                  {[
                    ['Policy Version', policyTrace.policy_version],
                    ['Policy Band', policyTrace.policy_band],
                    ['Adjustment Points', `${policyTrace.policy_adjustment_points >= 0 ? '+' : ''}${policyTrace.policy_adjustment_points}`],
                  ].map(([label, val]) => (
                    <div key={label} className="flex justify-between items-baseline py-1 border-b border-gray-50">
                      <span className="text-xs text-gray-500">{label}</span>
                      <span className="text-xs font-medium text-gray-900 font-mono">{val || 'N/A'}</span>
                    </div>
                  ))}
                </div>
                {(policyTrace.policy_reasons || []).length > 0 && (
                  <div className="mt-3">
                    <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">Policy Reasons</div>
                    <div className="space-y-1">
                      {policyTrace.policy_reasons.map((r, i) => (
                        <div key={i} className="text-xs text-gray-700 flex items-start gap-1.5">
                          <Info className="h-3 w-3 mt-0.5 text-purple-500 shrink-0" /> {r}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Missing data / pending verification flags */}
          {(modelTrace?.missing_reference_fields || []).length > 0 && (
            <Card className="border-amber-200">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <span className="font-semibold text-xs text-amber-800 uppercase tracking-wide">Missing Data Flags</span>
                </div>
                <div className="space-y-1">
                  {modelTrace.missing_reference_fields.map((f, i) => (
                    <div key={i} className="text-xs text-amber-700">{f}</div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <div className="flex items-center gap-2 justify-center p-4 bg-green-50 rounded-lg border border-green-200">
            <Award className="h-5 w-5 text-green-600" />
            <span className="font-semibold text-green-800">Assessment Scored Successfully</span>
          </div>

          {/* Post-scoring actions */}
          <div className="grid grid-cols-2 gap-3">
            {isOfficerOrAdmin && (
              <Button
                style={{ background: '#2563eb' }}
                onClick={async () => {
                  try {
                    await axios.post(`${API}/assessments/${assessment.id}/send-for-review`);
                    if (refreshAssessment) await refreshAssessment();
                    toast.success('Sent for officer review');
                    navigate(`/assessment/review/${assessment.id}`);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Failed');
                  }
                }}
                data-testid="send-for-review-btn"
              >
                <ClipboardCheck className="h-4 w-4 mr-2" /> Send for Review
              </Button>
            )}
            {isOfficerOrAdmin && (
              <Button
                variant="outline"
                onClick={() => navigate(`/assessment/review/${assessment.id}`)}
                data-testid="view-review-page-btn"
              >
                View Review Page
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2 justify-center p-4 bg-blue-50 rounded-lg border border-blue-200" data-testid="assessment-submitted-banner">
          <CheckCircle2 className="h-5 w-5 text-blue-600" />
          <span className="font-semibold text-blue-800">
            {isAgent
              ? 'Assessment sent to Credit Officer for scoring & review'
              : 'Assessment Submitted'}
          </span>
        </div>
      )}
    </div>
  );
};

export default ReviewSubmit;
